import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { IPatientInfo } from '../ipatient-info';
import { PatientAppointmentHistoryComponent } from '../../appointment/patient-appointment-history/patient-appointment-history.component';
import { PersonalDetailsComponent } from './personal-details/personal-details.component';
import { AddressDetailsComponent } from './address-details/address-details.component';
import { DialogUtils } from 'src/app/common/utils/dialog.utils';
import { MedicalRecordComponent } from './medical-record/medical-record.component';
import { AbdmHealthRecordsComponent } from './abdm-health-records/abdm-health-records.component';
import { LoginService } from 'src/app/login.service';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { PatientService } from '../patient.service';

@Component({
  selector: 'app-patient-view',
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    PatientAppointmentHistoryComponent,
    PersonalDetailsComponent,
    AddressDetailsComponent,
    MedicalRecordComponent,
    AbdmHealthRecordsComponent
  ],
  templateUrl: './patient-view.component.html',
  styleUrl: './patient-view.component.scss'
})
export class PatientViewComponent implements OnInit, OnDestroy {
  patientDetails: IPatientInfo | undefined;
  appointmentHistoryFlag = false;
  medicalRecordFlag = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: { patient?: IPatientInfo },
    private dialogRef: MatDialogRef<PatientViewComponent>,
    private dialogUtils: DialogUtils,
    private patientService: PatientService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private loginService: LoginService,
    private hospitalState: HospitalStateService
  ) { }

  ngOnInit(): void {
    this.patientDetails = this._data.patient
      ? {
        ...this._data.patient,
        address: this.normalizeAddresses(this._data.patient.address || [])
      }
      : undefined;

    this.loadViewSummary();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadViewSummary(): void {
    const patientId = Number(this.patientDetails?.id);
    if (!patientId) {
      return;
    }

    this.patientService.getPatientViewSummary(patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.appointmentHistoryFlag = response.result.hasAppointmentHistory;
          this.medicalRecordFlag = response.result.hasMedicalRecords;
        },
        error: () => {
          this.appointmentHistoryFlag = true;
          this.medicalRecordFlag = true;
          this.toastr.error(this.translate.instant('COMMON.ERROR'));
        }
      });
  }

  private normalizeAddresses(addresses: IPatientInfo['address'] = []): IPatientInfo['address'] {
    const explicitPermanentAddress = addresses.find(address => address?.isPermanent === true);
    const explicitCurrentAddress = addresses.find(address => address?.isPermanent === false);
    const untypedAddresses = addresses.filter(address => address?.isPermanent === undefined);

    const permanentAddress = explicitPermanentAddress ?? (!explicitCurrentAddress ? untypedAddresses[0] : undefined);
    const currentAddress = explicitCurrentAddress ??
      untypedAddresses.find(address => address !== permanentAddress);

    return [
      {
        id: permanentAddress?.id || 0,
        address1: permanentAddress?.address1 || '',
        address2: permanentAddress?.address2 || '',
        pincode: permanentAddress?.pincode || '',
        country: permanentAddress?.country || '',
        state: permanentAddress?.state || '',
        district: permanentAddress?.district || '',
        area: permanentAddress?.area || '',
        taluka: permanentAddress?.taluka || '',
        isPermanent: true
      },
      {
        id: currentAddress?.id || 0,
        address1: currentAddress?.address1 || '',
        address2: currentAddress?.address2 || '',
        pincode: currentAddress?.pincode || '',
        country: currentAddress?.country || '',
        state: currentAddress?.state || '',
        district: currentAddress?.district || '',
        area: currentAddress?.area || '',
        taluka: currentAddress?.taluka || '',
        isPermanent: false
      }
    ];
  }

  onHistoryLength(length: number): void {
    this.appointmentHistoryFlag = length > 0;
  }

  onMedicalRecordLength(length: number): void {
    this.medicalRecordFlag = length > 0;
  }

  get showAbdmHealthRecords(): boolean {
    const facilityId = this.hospitalState.getHospital()?.abdmFacilityId;
    return this.loginService.isAbdmHiuEnabled()
      && !!facilityId?.trim()
      && !!this.patientDetails?.abhaAddress;
  }

  get hasAddressOrContact(): boolean {
    const addressHasData = this.patientDetails?.address?.some(address =>
      address.address1 || address.address2 || address.area || address.country ||
      address.district || address.state || address.pincode || address.taluka
    );

    const contactHasData = this.patientDetails?.contactInfo?.some(contact =>
      contact.contact || contact.phone || contact.email
    );

    return !!(addressHasData || contactHasData);
  }

  onClose(): void {
    this.dialogUtils.closeSidePanel(this.dialogRef);
  }
}
